import { useState } from "react";
import Modal from "../../../Modals/Modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLightbulb,
  faShapes,
  faKeyboard,
} from "@fortawesome/free-solid-svg-icons";
import styles from "./Shapeguidemodal.module.css";
import { useLanguage } from "../../../../context/LanguageContext";

interface ShapeGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  primaryColor?: string;
  textColor?: string;
}

type TabType = "shapes" | "hotkeys";

const SHAPE_STROKE = "#333";
const SHAPE_FILL = "rgba(0, 0, 0, 0.05)";
const SHAPE_TEXT = "#1a1a1a";

export default function ShapeGuideModal({
  isOpen,
  onClose,
  primaryColor = "#ef4444",
  textColor = "#1a1a1a",
}: ShapeGuideModalProps) {
  const [activeTab, setActiveTab] = useState<TabType>("shapes");
  const { t } = useLanguage();

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t("shapeGuide")}>
      <div className={styles.modalBody}>
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === "shapes" ? styles.tabActive : ""}`}
            onClick={() => setActiveTab("shapes")}
            style={
              activeTab === "shapes"
                ? { borderBottomColor: primaryColor, color: primaryColor }
                : {}
            }
          >
            <FontAwesomeIcon icon={faShapes} />
            <span>{t("shapes")}</span>
          </button>
          <button
            className={`${styles.tab} ${activeTab === "hotkeys" ? styles.tabActive : ""}`}
            onClick={() => setActiveTab("hotkeys")}
            style={
              activeTab === "hotkeys"
                ? { borderBottomColor: primaryColor, color: primaryColor }
                : {}
            }
          >
            <FontAwesomeIcon icon={faKeyboard} />
            <span>{t("hotkeys")}</span>
          </button>
        </div>

        {activeTab === "shapes" && (
          <>
            <h3 className={styles.sectionTitle} style={{ color: primaryColor }}>
              {t("startAndEvents")}
            </h3>
            <div className={styles.section}>
              <div className={styles.shapeItem}>
                <svg width="120" height="80" className={styles.shapeSvg}>
                  <ellipse
                    cx="60"
                    cy="40"
                    rx="50"
                    ry="30"
                    fill={SHAPE_FILL}
                    stroke={SHAPE_STROKE}
                    strokeWidth="2"
                  />
                  <text
                    x="60"
                    y="45"
                    fill={SHAPE_TEXT}
                    fontSize="10"
                    textAnchor="middle"
                  >
                    {t("start")}
                  </text>
                </svg>
                <div className={styles.shapeDescription}>
                  <div className={styles.shapeTitle}>{t("ovalStart")}</div>
                  <p className={styles.shapeText}>{t("ovalStartDesc")}</p>
                </div>
              </div>

              <div className={styles.shapeItem}>
                <svg width="100" height="100" className={styles.shapeSvg}>
                  <polygon
                    points="10,20 80,60 10,100"
                    fill={SHAPE_FILL}
                    stroke={SHAPE_STROKE}
                    strokeWidth="2"
                  />
                  <text
                    x="35"
                    y="65"
                    fill={SHAPE_TEXT}
                    fontSize="10"
                    textAnchor="middle"
                  >
                    {t("event")}
                  </text>
                </svg>
                <div className={styles.shapeDescription}>
                  <div className={styles.shapeTitle}>{t("triangleEvent")}</div>
                  <p className={styles.shapeText}>{t("triangleEventDesc")}</p>
                </div>
              </div>
            </div>

            <h3 className={styles.sectionTitle} style={{ color: primaryColor }}>
              {t("basicShapes")}
            </h3>
            <div>
              <div className={styles.shapeItem}>
                <svg width="140" height="100" className={styles.shapeSvg}>
                  <rect
                    x="10"
                    y="10"
                    width="120"
                    height="80"
                    fill={SHAPE_FILL}
                    stroke={SHAPE_STROKE}
                    strokeWidth="2"
                    rx="3"
                  />
                  <text
                    x="70"
                    y="55"
                    fill={SHAPE_TEXT}
                    fontSize="12"
                    textAnchor="middle"
                  >
                    {t("primitive")}
                  </text>
                </svg>
                <div className={styles.shapeDescription}>
                  <div className={styles.shapeTitle}>
                    {t("rectanglePrimitive")}
                  </div>
                  <p className={styles.shapeText}>
                    {t("rectanglePrimitiveDesc")}
                  </p>
                </div>
              </div>

              <div className={styles.shapeItem}>
                <svg width="140" height="120" className={styles.shapeSvg}>
                  <polygon
                    points="70,10 130,60 70,110 10,60"
                    fill={SHAPE_FILL}
                    stroke={SHAPE_STROKE}
                    strokeWidth="2"
                  />
                  <text
                    x="70"
                    y="65"
                    fill={SHAPE_TEXT}
                    fontSize="11"
                    textAnchor="middle"
                  >
                    {t("condition")}
                  </text>
                </svg>
                <div className={styles.shapeDescription}>
                  <div className={styles.shapeTitle}>
                    {t("diamondCondition")}
                  </div>
                  <p className={styles.shapeText}>
                    {t("diamondConditionDesc")}
                  </p>
                </div>
              </div>

              <div className={styles.shapeItem}>
                <svg width="140" height="100" className={styles.shapeSvg}>
                  <polygon
                    points="30,10 110,10 130,50 110,90 30,90 10,50"
                    fill={SHAPE_FILL}
                    stroke={SHAPE_STROKE}
                    strokeWidth="2"
                  />
                  <text
                    x="70"
                    y="55"
                    fill={SHAPE_TEXT}
                    fontSize="12"
                    textAnchor="middle"
                  >
                    {t("loop")}
                  </text>
                </svg>
                <div className={styles.shapeDescription}>
                  <div className={styles.shapeTitle}>{t("hexagonLoop")}</div>
                  <p className={styles.shapeText}>{t("hexagonLoopDesc")}</p>
                </div>
              </div>

              <div className={styles.shapeItem}>
                <svg width="140" height="100" className={styles.shapeSvg}>
                  <rect
                    x="10"
                    y="20"
                    width="120"
                    height="60"
                    fill={SHAPE_FILL}
                    stroke={SHAPE_STROKE}
                    strokeWidth="2"
                    rx="30"
                  />
                  <text
                    x="70"
                    y="55"
                    fill={SHAPE_TEXT}
                    fontSize="12"
                    textAnchor="middle"
                  >
                    {t("break")}
                  </text>
                </svg>
                <div className={styles.shapeDescription}>
                  <div className={styles.shapeTitle}>
                    {t("roundedRectangleBreak")}
                  </div>
                  <p className={styles.shapeText}>
                    {t("roundedRectangleBreakDesc")}
                  </p>
                </div>
              </div>

              <div className={styles.shapeItem}>
                <svg width="140" height="100" className={styles.shapeSvg}>
                  <rect
                    x="10"
                    y="10"
                    width="120"
                    height="80"
                    fill={SHAPE_FILL}
                    stroke={SHAPE_STROKE}
                    strokeWidth="2"
                    rx="3"
                  />
                  <line
                    x1="10"
                    y1="22"
                    x2="130"
                    y2="22"
                    stroke={SHAPE_STROKE}
                    strokeWidth="1.5"
                  />
                  <line
                    x1="10"
                    y1="78"
                    x2="130"
                    y2="78"
                    stroke={SHAPE_STROKE}
                    strokeWidth="1.5"
                  />
                  <text
                    x="70"
                    y="55"
                    fill={SHAPE_TEXT}
                    fontSize="11"
                    textAnchor="middle"
                  >
                    {t("function")}
                  </text>
                </svg>
                <div className={styles.shapeDescription}>
                  <div className={styles.shapeTitle}>
                    {t("functionCallShape")}
                  </div>
                  <p className={styles.shapeText}>
                    {t("functionCallShapeDesc")}
                  </p>
                </div>
              </div>

              <div className={styles.shapeItem}>
                <svg width="140" height="100" className={styles.shapeSvg}>
                  <rect
                    x="10"
                    y="20"
                    width="120"
                    height="60"
                    fill={SHAPE_FILL}
                    stroke={SHAPE_STROKE}
                    strokeWidth="2"
                    rx="10"
                  />
                  <path
                    d="M 45 50 L 30 50 M 30 50 L 38 43 M 30 50 L 38 57"
                    stroke={SHAPE_STROKE}
                    strokeWidth="2"
                    fill="none"
                  />
                  <text
                    x="80"
                    y="55"
                    fill={SHAPE_TEXT}
                    fontSize="11"
                    textAnchor="middle"
                  >
                    {t("return")}
                  </text>
                </svg>
                <div className={styles.shapeDescription}>
                  <div className={styles.shapeTitle}>{t("returnShape")}</div>
                  <p className={styles.shapeText}>{t("returnShapeDesc")}</p>
                </div>
              </div>

              <div className={styles.shapeItem}>
                <svg width="140" height="100" className={styles.shapeSvg}>
                  <polygon
                    points="40,10 100,10 130,30 130,70 100,90 40,90 10,70 10,30"
                    fill={SHAPE_FILL}
                    stroke={SHAPE_STROKE}
                    strokeWidth="2"
                  />
                  <text
                    x="70"
                    y="55"
                    fill={SHAPE_TEXT}
                    fontSize="11"
                    textAnchor="middle"
                  >
                    {t("throw")}
                  </text>
                </svg>
                <div className={styles.shapeDescription}>
                  <div className={styles.shapeTitle}>{t("throwShape")}</div>
                  <p className={styles.shapeText}>{t("throwShapeDesc")}</p>
                </div>
              </div>

              <div className={styles.shapeItem}>
                <svg width="140" height="100" className={styles.shapeSvg}>
                  <rect
                    x="10"
                    y="10"
                    width="120"
                    height="80"
                    fill="none"
                    stroke={SHAPE_STROKE}
                    strokeWidth="2"
                    rx="3"
                  />
                  <rect
                    x="16"
                    y="16"
                    width="108"
                    height="68"
                    fill={SHAPE_FILL}
                    stroke={SHAPE_STROKE}
                    strokeWidth="1.5"
                    rx="2"
                  />
                  <text
                    x="70"
                    y="55"
                    fill={SHAPE_TEXT}
                    fontSize="10"
                    textAnchor="middle"
                  >
                    {t("subtransaction")}
                  </text>
                </svg>
                <div className={styles.shapeDescription}>
                  <div className={styles.shapeTitle}>
                    {t("subtransactionShape")}
                  </div>
                  <p className={styles.shapeText}>
                    {t("subtransactionShapeDesc")}
                  </p>
                </div>
              </div>
            </div>

            <div
              className={styles.tipsBox}
              style={{ borderColor: primaryColor }}
            >
              <h4 className={styles.tipsTitle} style={{ color: primaryColor }}>
                <FontAwesomeIcon icon={faLightbulb} />
                <span>{t("namingTips")}</span>
              </h4>
              <p className={styles.tipsText}>{t("namingTipsDesc")}</p>
              <ul className={styles.tipsList}>
                <li>
                  <code>transaction_start</code> → {t("ovalShape")}
                </li>
                <li>
                  <code>source_message</code> → {t("triangleShape")}
                </li>
                <li>
                  <code>process_item</code> → {t("rectangleShape")}
                </li>
                <li>
                  <code>if condition</code> → {t("diamondShape")}
                </li>
                <li>
                  <code>for i in items</code> → {t("hexagonShape")}
                </li>
                <li>
                  <code>break</code> → {t("roundedRectangleShape")}
                </li>
                <li>
                  <code>call_function()</code> → {t("functionShape")}
                </li>
                <li>
                  <code>return value</code> → {t("returnShapeTip")}
                </li>
                <li>
                  <code>throw new Error()</code> → {t("throwShapeTip")}
                </li>
                <li>
                  <code>run_subtransaction</code> →{" "}
                  {t("subtransactionShapeTip")}
                </li>
              </ul>
            </div>
          </>
        )}

        {activeTab === "hotkeys" && (
          <div className={styles.hotkeysContent}>
            <h3 className={styles.sectionTitle} style={{ color: primaryColor }}>
              {t("general")}
            </h3>
            <div className={styles.hotkeysList}>
              <div className={styles.hotkeyItem}>
                <kbd className={styles.kbd}>Ctrl</kbd> +{" "}
                <kbd className={styles.kbd}>E</kbd>
                <span className={styles.hotkeyDescription}>
                  {t("toggleEditMode")}
                </span>
              </div>
              <div className={styles.hotkeyItem}>
                <kbd className={styles.kbd}>Ctrl</kbd> +{" "}
                <kbd className={styles.kbd}>S</kbd>
                <span className={styles.hotkeyDescription}>
                  {t("saveTransaction")}
                </span>
              </div>
            </div>

            <h3 className={styles.sectionTitle} style={{ color: primaryColor }}>
              {t("navigationBetweenShapes")}
            </h3>
            <div className={styles.hotkeysList}>
              <div className={styles.hotkeyItem}>
                <kbd className={styles.kbd}>{t("click")}</kbd>
                <span className={styles.hotkeyDescription}>
                  {t("selectShape")}
                </span>
              </div>
              <div className={styles.hotkeyItem}>
                <kbd className={styles.kbd}>Alt</kbd> +{" "}
                <kbd className={styles.kbd}>←</kbd>
                <span className={styles.hotkeyDescription}>
                  {t("moveLeft")}
                </span>
              </div>
              <div className={styles.hotkeyItem}>
                <kbd className={styles.kbd}>Alt</kbd> +{" "}
                <kbd className={styles.kbd}>→</kbd>
                <span className={styles.hotkeyDescription}>
                  {t("moveRight")}
                </span>
              </div>
              <div className={styles.hotkeyItem}>
                <kbd className={styles.kbd}>Alt</kbd> +{" "}
                <kbd className={styles.kbd}>↑</kbd>
                <span className={styles.hotkeyDescription}>{t("moveUp")}</span>
              </div>
              <div className={styles.hotkeyItem}>
                <kbd className={styles.kbd}>Alt</kbd> +{" "}
                <kbd className={styles.kbd}>↓</kbd>
                <span className={styles.hotkeyDescription}>
                  {t("moveDown")}
                </span>
              </div>
              <div className={styles.hotkeyItem}>
                <kbd className={styles.kbd}>Esc</kbd> /{" "}
                <kbd className={styles.kbd}>Backspace</kbd>
                <span className={styles.hotkeyDescription}>
                  {t("deleteShape")}
                </span>
              </div>
              <div className={styles.hotkeyItem}>
                <kbd className={styles.kbd}>{t("clickCanvas")}</kbd>
                <span className={styles.hotkeyDescription}>
                  {t("deselectShape")}
                </span>
              </div>
            </div>

            <h3 className={styles.sectionTitle} style={{ color: primaryColor }}>
              {t("addingShapes")}
            </h3>
            <div className={styles.hotkeysList}>
              <div className={styles.hotkeyItem}>
                <kbd className={styles.kbd}>Ctrl</kbd> +{" "}
                <kbd className={styles.kbd}>→</kbd>
                <span className={styles.hotkeyDescription}>
                  {t("addShapeAfter")}
                </span>
              </div>
              <div className={styles.hotkeyItem}>
                <kbd className={styles.kbd}>Ctrl</kbd> +{" "}
                <kbd className={styles.kbd}>↓</kbd>
                <span className={styles.hotkeyDescription}>
                  {t("addShapeYesBranch")}
                </span>
              </div>
              <div className={styles.hotkeyItem}>
                <kbd className={styles.kbd}>Ctrl</kbd> +{" "}
                <kbd className={styles.kbd}>↑</kbd>
                <span className={styles.hotkeyDescription}>
                  {t("addShapeNoBranch")}
                </span>
              </div>
            </div>

            <h3 className={styles.sectionTitle} style={{ color: primaryColor }}>
              {t("shapeSelectionInModal")}
            </h3>
            <div className={styles.hotkeysList}>
              <div className={styles.hotkeyItem}>
                <kbd className={styles.kbd}>1</kbd>
                <span className={styles.hotkeyDescription}>
                  {t("selectPrimitiveShape")}
                </span>
              </div>
              <div className={styles.hotkeyItem}>
                <kbd className={styles.kbd}>2</kbd>
                <span className={styles.hotkeyDescription}>
                  {t("selectConditionShape")}
                </span>
              </div>
              <div className={styles.hotkeyItem}>
                <kbd className={styles.kbd}>3</kbd>
                <span className={styles.hotkeyDescription}>
                  {t("selectLoopShape")}
                </span>
              </div>
              <div className={styles.hotkeyItem}>
                <kbd className={styles.kbd}>4</kbd>
                <span className={styles.hotkeyDescription}>
                  {t("selectBreakShape")}
                </span>
              </div>
              <div className={styles.hotkeyItem}>
                <kbd className={styles.kbd}>5</kbd>
                <span className={styles.hotkeyDescription}>
                  {t("selectFunctionShape")}
                </span>
              </div>
              <div className={styles.hotkeyItem}>
                <kbd className={styles.kbd}>6</kbd>
                <span className={styles.hotkeyDescription}>
                  {t("selectReturnShape")}
                </span>
              </div>
              <div className={styles.hotkeyItem}>
                <kbd className={styles.kbd}>7</kbd>
                <span className={styles.hotkeyDescription}>
                  {t("selectThrowShape")}
                </span>
              </div>
              <div className={styles.hotkeyItem}>
                <kbd className={styles.kbd}>8</kbd>
                <span className={styles.hotkeyDescription}>
                  {t("selectSubtransactionShape")}
                </span>
              </div>
            </div>

            <h3 className={styles.sectionTitle} style={{ color: primaryColor }}>
              {t("editingShapes")}
            </h3>
            <div className={styles.hotkeysList}>
              <div className={styles.hotkeyItem}>
                <kbd className={styles.kbd}>1–8</kbd>
                <span className={styles.hotkeyDescription}>
                  {t("openEditPanel")}
                </span>
              </div>
              <div className={styles.hotkeyItem}>
                <kbd className={styles.kbd}>Enter</kbd>
                <span className={styles.hotkeyDescription}>
                  {t("saveAndClosePanel")}
                </span>
              </div>
              <div className={styles.hotkeyItem}>
                <kbd className={styles.kbd}>Esc</kbd>
                <span className={styles.hotkeyDescription}>
                  {t("closeEditPanel")}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className={styles.modalFooter}>
          <button className={styles.saveModalButton} onClick={onClose}>
            {t("gotIt")}
          </button>
        </div>
      </div>
    </Modal>
  );
}
